import {createAsyncThunk, createSlice, PayloadAction} from '@reduxjs/toolkit';
import tokenService from '../../_services/token.service';
import axios from 'axios';
import { config } from '../../_config/config';
import { AssignedCandidate } from '../../models/assigned-candidate.interface';
import { toast } from 'react-toastify';
import i18n from '../../i18n/i18n';
import { MatchPayload } from '../../models/match-payload';
import { matchStarted } from '../../constants/constants';
import {getCandidateNumberByStatus} from "../../utils/get-candidate-status-number";
import { RootState } from '../store';

type SortDir = 'asc' | 'desc' | null;
type SortState = { user_status: SortDir; duration: SortDir; experience: SortDir, name: SortDir, position: SortDir, ai_match: SortDir };
type FilterState = {
  offer_id: string | null;
  status: string | null;
  languageIds: string[];
  skillIds: string[];
  cities: string[];
  unseen: boolean;
  minMatch: number | null;
  daysAgo: number | null;
};

interface CompanyCandidatesSlice {
  assignedCandidates: AssignedCandidate[];
  total: number;
  page: number;
  size: number;
  searchValue?: string;
  sort: SortState;
  filters: FilterState;
  filterOptions: { cities: string[]; skills: { id: string; name: string }[] };
}

const initialState: CompanyCandidatesSlice = {
  assignedCandidates: [],
  total: 0,
  page: 1,
  size: 10,
  sort: { user_status: null, duration: null, experience: null, name: null, position: null, ai_match: null } as SortState,
  filters: {
    offer_id: null, status: null,
    languageIds: [], skillIds: [], cities: [],
    unseen: false, minMatch: null, daysAgo: null,
  } as FilterState,
  filterOptions: { cities: [], skills: [] },
};

export const getAssignedCandidates = createAsyncThunk(
    'companyCandidates/fetch',
    async (payload: {
        pageNumber?: number;
        searchValue?: string;
        statusValue?: string;
        size?: number;
        offer_id?: string | null;
        userStatus?: 'asc' | 'desc';
        duration?: 'asc' | 'desc';
        experience?: 'asc' | 'desc';
        name?: 'asc' | 'desc';
        position?: 'asc' | 'desc';
        aiMatch?: 'asc' | 'desc';
        languageIds?: string[];
        skillIds?: string[];
        cities?: string[];
        unseen?: boolean;
        minMatch?: number | null;
        daysAgo?: number | null;
    } = {}, { getState, /* ...rest */ }) => {
        const s = (getState() as RootState).companyCandidatesReducer;

        // Use provided values if present, otherwise fall back to saved state
        const page = payload.pageNumber ?? s.page;
        const size = payload.size ?? s.size;
        const searchValue = payload.searchValue ?? s.searchValue;
        const offer_id = (payload.offer_id ?? s.filters.offer_id) ?? '';
        const status = (payload.statusValue ?? s.filters.status) ?? '';

        const user_status = payload.userStatus ?? s.sort.user_status ?? undefined;
        const duration    = payload.duration    ?? s.sort.duration    ?? undefined;
        const experience  = payload.experience  ?? s.sort.experience  ?? undefined;
        const name  = payload.name  ?? s.sort.name  ?? undefined;
        const position  = payload.position  ?? s.sort.position  ?? undefined;
        const ai_match  = payload.aiMatch  ?? s.sort.ai_match  ?? undefined;

        const languageIds = payload.languageIds ?? s.filters.languageIds ?? [];
        const skillIds    = payload.skillIds    ?? s.filters.skillIds    ?? [];
        const cities      = payload.cities      ?? s.filters.cities      ?? [];
        const unseen      = payload.unseen      ?? s.filters.unseen      ?? false;
        const minMatch    = payload.minMatch    ?? s.filters.minMatch    ?? null;
        const daysAgo     = payload.daysAgo     ?? s.filters.daysAgo     ?? null;

        const params: Record<string, any> = { page, size };
        // Parameters
        if (offer_id) params.offer_id = offer_id;
        if (status) params.status = getCandidateNumberByStatus(status);
        // Sorts
        if (user_status) params.user_status = user_status;
        if (duration)    params.duration    = duration;
        if (experience)  params.experience  = experience;
        //if (searchValue) params.search = searchValue; // Not used?
        if (name) params.name = name;
        if (position) params.position = position;
        if (ai_match) params.ai_match = ai_match;
        // Advanced filters
        if (languageIds.length) params.language_ids = languageIds.join(',');
        if (skillIds.length)    params.skill_ids    = skillIds.join(',');
        if (cities.length)      params.cities       = cities.join(',');
        if (unseen)             params.unseen       = 'true';
        if (minMatch !== null)  params.min_match    = minMatch;
        if (daysAgo !== null)   params.days_ago     = daysAgo;

        //console.log(params);

        // keep your existing URL and axios call style
        const token = tokenService.getLocalAccessToken();
        const { data } = await axios.get(config.SERVER_URL + 'candidate/company_list/', { params, headers: {
                Authorization: 'Bearer ' + token,
            },
        });

        return data; // keep your original return
    }
);


export const getCompanyCandidateFilterOptions = createAsyncThunk(
  'companyCandidates/filterOptions',
  async () => {
    const token = tokenService.getLocalAccessToken();
    const { data } = await axios.get(
      config.SERVER_URL + 'candidate/company_filter_options/',
      { headers: { Authorization: 'Bearer ' + token } }
    );
    return data as { cities: string[]; skills: { id: string; name: string }[] };
  }
);

export const setCandidateStatus = createAsyncThunk(
  '/companyCandidates/setCandidateStatus',

  async ({
    offer_id,
    candidate_id,
    status
  }: {
    offer_id: string;
    candidate_id: string;
    status: number;
  }) => {
    const token = tokenService.getLocalAccessToken();
    const response = await axios.put(
      config.SERVER_URL + 'offer/applied/change/status/' + offer_id + '/' + candidate_id,
      { status: status },
      {
        headers: {
          Authorization: 'Bearer ' + token,
        },
      }
    );

    return response.data;
  }
);

export const setCandidateSeen = createAsyncThunk(
  '/companyCandidates/setCandidateSeen',

  async ({
    offer_id,
    candidate_id,
    is_seen
  }: {
    offer_id: string;
    candidate_id: string;
    is_seen: boolean;
  }) => {
    const token = tokenService.getLocalAccessToken();
    const response = await axios.put(
      config.SERVER_URL + 'offer/applied/change/seen/' + offer_id + '/' + candidate_id + '/',
      { is_seen: is_seen },
      {
        headers: {
          Authorization: 'Bearer ' + token,
        },
      }
    );

    return response.data;
  }
);


export const saveCandidate = createAsyncThunk(
  '/companyCandidates/saveCandidate',
  async (id: string) => {
    const token = tokenService.getLocalAccessToken();
    const response = await axios.post(
      config.SERVER_URL + 'company/save/candidate/',
      { candidate: id },
      {
        headers: {
          Authorization: 'Bearer ' + token,
        },
      }
    );

    return response.data;
  }
);

export const unsaveCandidate = createAsyncThunk(
  '/companyCandidates/unsaveCandidate',
  async (id: string) => {
    const token = tokenService.getLocalAccessToken();
    const response = await axios.delete(
      config.SERVER_URL + 'company/save/candidate/' + id + '/',
      {
        headers: {
          Authorization: 'Bearer ' + token,
        },
      }
    );

    return response.data;
  }
);

export const calculateCandidateMatch = createAsyncThunk(
  '/companyCandidate/calculateMatch',
  async ({
    matchArr,
    selectedMatchIds,
  }: {
    matchArr: MatchPayload[];
    selectedMatchIds: string[];
  }) => {
    const token = tokenService.getLocalAccessToken();
    await axios.post(
      `${config.SERVER_URL}candidate/ai-match/`,
      { data: matchArr },
      {
        headers: {
          Authorization: 'Bearer ' + token,
        },
      }
    );

    return selectedMatchIds;
  }
);

const companyCandidatesSlice = createSlice({
  name: 'companyCandidates',
  initialState,
  reducers: {
    updateMatchDataById: (state, action) => {
      const index = state.assignedCandidates.findIndex(
        (candidate) => candidate.id === action.payload.id
      );
      if (index >= 0) {
        state.assignedCandidates[index].ai_match?.unshift(
          action.payload.ai_match
        );
      }
    },
      setPage(state, action: PayloadAction<number>) {
          state.page = Math.max(1, action.payload || 1);
      },
      setSize(state, action: PayloadAction<number>) {
          state.size = Math.max(1, action.payload || 10);
          state.page = 1; // optional: reset to first page when page size changes
      },
      setUserStatusSort(state, action: PayloadAction<SortDir>) {
          state.sort.user_status = action.payload;
          // Clear the other sorts when user_status is set - if we want to allow sorting by multiple fields, remove this
          state.sort.duration = null;
          state.sort.experience = null;
          state.sort.name = null;
          state.sort.position = null;
          state.sort.ai_match = null;
      },
      setDurationSort(state, action: PayloadAction<SortDir>) {
          state.sort.duration = action.payload;
          //clear the other sorts
          state.sort.user_status = null;
          state.sort.experience = null;
          state.sort.name = null;
          state.sort.position = null;
          state.sort.ai_match = null;
      },
      setExperienceSort(state, action: PayloadAction<SortDir>) {
          state.sort.experience = action.payload;
          // Clear the other sorts
          state.sort.user_status = null;
          state.sort.duration = null;
          state.sort.name = null;
          state.sort.position = null;
          state.sort.ai_match = null;
      },
      setNameSort(state, action: PayloadAction<SortDir>) {
          state.sort.name = action.payload;
          // Clear the other sorts
          state.sort.user_status = null;
          state.sort.duration = null;
          state.sort.experience = null;
          state.sort.position = null;
          state.sort.ai_match = null;
      },
      setPositionSort(state, action: PayloadAction<SortDir>) {
          state.sort.position = action.payload;
          // Clear the other sorts
          state.sort.user_status = null;
          state.sort.duration = null;
          state.sort.name = null;
          state.sort.experience = null;
          state.sort.ai_match = null;
      },
      setAIMatchSort(state, action: PayloadAction<SortDir>) {
          state.sort.ai_match = action.payload;
          // Clear the other sorts
          state.sort.user_status = null;
          state.sort.duration = null;
          state.sort.name = null;
          state.sort.experience = null;
          state.sort.position = null;
      },
      setOfferFilter(state, action: PayloadAction<string | null>) {
          state.filters.offer_id = action.payload;
      },
      setStatusFilter(state, action: PayloadAction<string | null>) {
          state.filters.status = action.payload;
      },
      setLanguageIdsFilter(state, action: PayloadAction<string[]>) {
          state.filters.languageIds = action.payload;
      },
      setSkillIdsFilter(state, action: PayloadAction<string[]>) {
          state.filters.skillIds = action.payload;
      },
      setCitiesFilter(state, action: PayloadAction<string[]>) {
          state.filters.cities = action.payload;
      },
      setUnseenFilter(state, action: PayloadAction<boolean>) {
          state.filters.unseen = action.payload;
      },
      setMinMatchFilter(state, action: PayloadAction<number | null>) {
          state.filters.minMatch = action.payload;
      },
      setDaysAgoFilter(state, action: PayloadAction<number | null>) {
          state.filters.daysAgo = action.payload;
      },
  },

  extraReducers(builder) {
    builder
      .addCase(getAssignedCandidates.fulfilled, (state, action) => {
        state.total = action.payload.body.totalElements;
        state.assignedCandidates = action.payload.body.data;
        state.assignedCandidates.map((item) => (item.checked = false));
      })
      .addCase(getCompanyCandidateFilterOptions.fulfilled, (state, action) => {
        state.filterOptions = action.payload;
      })
      .addCase(calculateCandidateMatch.fulfilled, (state, action) => {
        const selectedMatchIds = action.payload;
        selectedMatchIds.map((id) => {
          const selectedItemIndex = state.assignedCandidates.findIndex(
            (cand) => cand.id === id
          );

          state.assignedCandidates[selectedItemIndex].ai_match?.unshift({
            process_status: matchStarted,
            is_prematch: false,
          });
        });

        toast.success(i18n.t('messages.matchMessage'));
      });
  },
});

export const {
    updateMatchDataById,
    setUserStatusSort,
    setDurationSort,
    setExperienceSort,
    setNameSort,
    setPositionSort,
    setAIMatchSort,
    setOfferFilter,
    setStatusFilter,
    setLanguageIdsFilter,
    setSkillIdsFilter,
    setCitiesFilter,
    setUnseenFilter,
    setMinMatchFilter,
    setDaysAgoFilter,
} = companyCandidatesSlice.actions;
export default companyCandidatesSlice.reducer;